import type { SpecPolicyReason } from '@parcel/tools';
import type { SpecToolDecision } from '@parcel/types';
import { type SpecToolSettingsRow, deriveSpecToolSummary } from '../../lib/spec-tools.js';
import { SpecToolPolicyControl } from './spec-tool-policy-control.js';
import { SpecToolRowShell } from './spec-tool-row-shell.js';

export interface SpecToolRowProps {
  row: SpecToolSettingsRow;
  /**
   * `workspace`: an owner/admin editing the workspace default. `member`: any
   * member tightening their own posture, where the control shows what is in
   * effect for them and a Reset appears once they have an override of their own.
   */
  mode: 'workspace' | 'member';
  /**
   * The window `row.recentAuditCount` counts over, straight from the same list
   * response. Required, not defaulted: a count with no window ("5 recent Spec
   * actions") cannot be read -- 5 ever, or 5 this month? -- and a default here
   * would be this file guessing at a number the API already sent.
   */
  auditWindowDays: number;
  /**
   * True when the viewer is an owner/admin. On the personal layer it lets them
   * loosen their own posture back to the registry floor, because the workspace
   * rule they would otherwise be bound by is one they set themselves.
   */
  outranksWorkspace?: boolean;
  /** True while a write for the layer `mode` names is in flight. */
  saving?: boolean;
  /** Inline error for that layer's last write attempt, if it failed. */
  error?: string | null;
  /** Fires when the selection changes. `null` clears a personal override. */
  onChange: (decision: SpecToolDecision | null) => void;
  className?: string;
}

/**
 * One sentence per `SpecPolicyReason`, plus a fallback for a value this build
 * does not recognize -- a future server-side reason must never render an empty
 * explanation.
 */
const REASON_SENTENCES: Readonly<Record<SpecPolicyReason, string>> = {
  launch_off: 'Spec is turned off for this workspace right now, so no tool can run.',
  read_only: 'Spec is in read-only mode right now, so no tool can run.',
  kill_switch: 'This tool is temporarily disabled workspace-wide.',
  registry_floor: `This tool's built-in safety floor sets this decision.`,
  workspace_policy: `Set by this workspace's policy for this tool.`,
  member_policy: 'Set by your personal override for this tool.',
  default_policy: `Using this tool's default policy; no override is set.`,
  unknown_tool: 'Parcel does not recognize this tool, so it is blocked.',
  chat_switch: 'Web is switched off for this chat.',
  small_spend: 'An enrichment spend under the card threshold runs without asking.',
};

export function describeReason(reason: SpecPolicyReason): string {
  return REASON_SENTENCES[reason] ?? `This decision follows Spec's safety policy.`;
}

// UTC, matching every other calendar date in this app: these are UTC
// instants, and CI runs with TZ=UTC (see billing-settings.ts, signal-dates.ts).
const LAST_CHANGED_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * `null` when there is nothing to report (`lastChangedAt` unset). A missing
 * display name never surfaces the raw member uuid -- it drops the "by" clause
 * instead.
 */
export function describeLastChanged(
  row: Pick<SpecToolSettingsRow, 'lastChangedAt' | 'lastChangedBy'>,
): string | null {
  if (!row.lastChangedAt) return null;
  const date = LAST_CHANGED_DATE_FORMAT.format(new Date(row.lastChangedAt));
  const name = row.lastChangedBy?.displayName;
  return name ? `Changed by ${name} on ${date}` : `Changed on ${date}`;
}

/**
 * `0` is "never used", not absence -- callers must check `recentAuditCount !==
 * null` first.
 *
 * Every phrasing names the window, including the zero one: "Never used
 * recently" left an admin unable to tell a tool nobody has ever called from one
 * that was last called five weeks ago.
 */
export function describeRecentAuditCount(count: number, windowDays: number): string {
  const window = `the last ${windowDays} ${windowDays === 1 ? 'day' : 'days'}`;
  if (count === 0) return `Never used in ${window}`;
  return count === 1 ? `1 Spec action in ${window}` : `${count} Spec actions in ${window}`;
}

/**
 * Everything about a tool that is NOT its current setting.
 *
 * The row itself is deliberately one line: at 36 tools, a description, an
 * effect badge, an effective-decision heading, a provenance line and a usage
 * count per row is the wall of configuration this layout exists to remove. None
 * of it is deleted, it is demoted -- the tool's own description leads, so the
 * first thing a hover answers is "what does this actually do".
 *
 * Returned as parts rather than one string because this renders into a real
 * `Tooltip` surface: the browser's native `title` stacked all of it as one
 * undifferentiated grey block with no way to weight the description above the
 * metadata.
 */
export function buildSpecToolTooltip(
  row: SpecToolSettingsRow,
  auditWindowDays: number,
): { description: string; details: string[] } {
  // The registry description's FIRST SENTENCE only. The rest is written for the
  // model, not for the person configuring it, and pasting all of it made the
  // hover a wall of calling technique and credit notes.
  //
  // The effective-decision reason is deliberately absent: with the page's own
  // visibility guard in place the interesting cases (launch off, kill switch)
  // cannot reach this list, leaving the same "no override is set" sentence
  // repeated down all 36 rows.
  const details = [row.name];
  const lastChanged = describeLastChanged(row);
  if (lastChanged) details.push(lastChanged);
  if (row.recentAuditCount !== null) {
    details.push(describeRecentAuditCount(row.recentAuditCount, auditWindowDays));
  }
  return { description: deriveSpecToolSummary(row.description), details };
}

/**
 * One line of the Tools list: the tool, and the decision the viewer controls.
 *
 * Pure: props in, `onChange` out. No fetching, no react-query, no server state,
 * and no visibility filtering of its own -- a blocked-by-floor tool renders
 * exactly like any other, because this component never hides a row it is given.
 */
export function SpecToolRow({
  row,
  mode,
  auditWindowDays,
  outranksWorkspace = false,
  saving = false,
  error = null,
  onChange,
  className,
}: SpecToolRowProps) {
  // EVERY row shows a selection, in both modes. An unset layer is not a blank
  // state, it is the layer under it showing through: a workspace that has never
  // configured `search_accounts` is not undecided about it, the tool is running
  // right now under its registry default.
  //
  // The fallback is the tool's own floor rather than a fixed "needs approval",
  // because for all 36 native tools `defaultPolicy === registryFloor` (asserted
  // in packages/tools/src/native-registry.test.ts), so the floor IS what the
  // server applies to an unset row. Showing a blanket "needs approval" instead
  // would display a stricter posture than the one actually in force, and an
  // admin reading it would think reads were already gated when they are not.
  const value =
    mode === 'workspace'
      ? (row.workspaceDecision ?? row.registryFloor)
      : (row.memberDecision ?? row.workspaceDecision ?? row.registryFloor);
  const overridden = mode === 'member' && row.memberDecision !== null;
  const tooltip = buildSpecToolTooltip(row, auditWindowDays);

  return (
    <SpecToolRowShell title={row.title} tooltip={tooltip} className={className}>
      {/*
          Visible text stays one word at this density, but the accessible name
          names the tool: 36 rows of an identically-labelled "Reset" is a screen
          reader reading the same button 36 times with no way to tell which one
          it is on. The button's mere presence is also what marks this row as
          overridden, which is why there is no separate badge saying so.
        */}
      <SpecToolPolicyControl
        row={row}
        scope={mode}
        value={value}
        disabled={false}
        outranksWorkspace={outranksWorkspace}
        saving={saving}
        error={error}
        onReset={overridden ? () => onChange(null) : undefined}
        resetLabel={`Reset ${row.title} to the workspace default`}
        onChange={onChange}
      />
    </SpecToolRowShell>
  );
}
